/**
 * Surface identity for every Amplitude event this app emits.
 * See shield-core/docs/amplitude-surface-taxonomy.md.
 *
 * Stamped by the enrichment plugin in ./surface-plugin rather than by each
 * track() call, for two reasons: autocapture events (page views, sessions)
 * never pass through our own helpers and would otherwise ship without a
 * surface, and a per-call convention drifts the moment someone adds an event
 * and forgets.
 *
 * Deliberately NOT an identify() user property: a user operates in this
 * dashboard and in Rapid the same week, and a user property is last-write-wins
 * — it would relabel that user's entire history retroactively. That is the bug
 * the existing `Platform` user property has.
 */
export const SURFACE_ID = "web_service_a" as const;
export const SURFACE_TYPE = "web" as const;
export const APPLICATION_ID = "shield-interface" as const;

/**
 * Set by a sibling surface (Rapid) before it delegates login to this origin,
 * so the signup lands attributed to where the journey actually started.
 * Scoped to `.getshield.xyz`, short-lived — same pattern as `product_intent`.
 */
export const SURFACE_INTENT_COOKIE = "surface_intent";

/** The only values the taxonomy admits. Anything else is dropped, not passed through. */
export const SURFACE_IDS = [
  "landing",
  "web_service_a",
  "web_service_b",
  "mobile_app",
  "checkout"
] as const;

export type SurfaceId = (typeof SURFACE_IDS)[number];

export function isSurfaceId(value: string | undefined): value is SurfaceId {
  return !!value && (SURFACE_IDS as readonly string[]).includes(value);
}

/** surface_type and application_id that go with a given surface_id. */
export const SURFACE_METADATA: Record<
  SurfaceId,
  { surface_type: string; application_id: string }
> = {
  landing: { surface_type: "landing", application_id: "shield-landing-next" },
  web_service_a: { surface_type: "web", application_id: "shield-interface" },
  web_service_b: { surface_type: "web", application_id: "shield-basic" },
  mobile_app: { surface_type: "app", application_id: "shield-ios" },
  checkout: { surface_type: "hosted", application_id: "shield-interface" }
};

/**
 * Staging deploys run with NODE_ENV=production, so NODE_ENV alone cannot
 * separate them inside a single Amplitude project. NEXT_PUBLIC_APP_ENV is the
 * explicit override; VERCEL_ENV covers preview deploys without extra config.
 */
export const ENVIRONMENT =
  process.env.NEXT_PUBLIC_APP_ENV ||
  process.env.NEXT_PUBLIC_VERCEL_ENV ||
  process.env.NODE_ENV ||
  "development";
